import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  Typography,
  Grid,
  IconButton,
  Divider,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, ExpandMore as ExpandMoreIcon, Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose }) => {
  const { user, updateUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Validate form data
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('First name and last name are required');
        return;
      }

                                          // Call API to update user profile (username is immutable)
                  const response = await api.put('/user/profile', formData);

      const updatedUser = response.data;
      
      // Update the user context
      if (updateUser) {
        updateUser(updatedUser);
      }

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Validate password fields
      if (!passwordData.currentPassword.trim()) {
        setError('Current password is required');
        return;
      }
      if (!passwordData.newPassword.trim()) {
        setError('New password is required');
        return;
      }
      if (!passwordData.confirmPassword.trim()) {
        setError('Password confirmation is required');
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      // Validate password strength (same as backend policy)
      if (passwordData.newPassword.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (!/[A-Z]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one uppercase letter');
        return;
      }
      if (!/[a-z]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one lowercase letter');
        return;
      }
      if (!/[0-9]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one digit');
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)) {
        setError('Password must contain at least one special character');
        return;
      }

      // Call API to change password
      const response = await api.post('/user/change-password', passwordData);

      setSuccess('Password changed successfully! You will be logged out and redirected to login page.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close the profile dialog
      onClose();

      // Redirect to login page after a short delay
      setTimeout(() => {
        // Clear auth context and redirect to login
        logout();
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    }
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Avatar loading logic
  useEffect(() => {
    if (!user) return;

    const fetchAvatar = async () => {
      try {
        // Only fetch avatar if user.avatarUrl is a relative path
        if (user.avatarUrl && user.avatarUrl.startsWith('/')) {
          const response = await api.get('/user/avatar', {
            responseType: 'blob',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
          const dataUrl = URL.createObjectURL(blob);
          setAvatarDataUrl(dataUrl);
        } else if (user.avatarUrl) {
          // If it's a full URL (like Google profile), use it directly
          setAvatarDataUrl(null);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        setAvatarDataUrl(null);
      }
    };

    fetchAvatar();

    return () => {
      if (avatarDataUrl) {
        URL.revokeObjectURL(avatarDataUrl);
      }
    };
  }, [user?.id]); // Only depend on user ID

  const getAvatarUrl = () => {
    // If user has a full URL (like Google profile), use it directly
    if (user?.avatarUrl && !user.avatarUrl.startsWith('/')) {
      return user.avatarUrl;
    }
    // Otherwise use the blob URL if we have one
    return avatarDataUrl || undefined;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">User Profile</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={getAvatarUrl()}
            sx={{ 
              width: 80, 
              height: 80, 
              fontSize: '2rem',
              mr: 2
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user?.username || 'User'
              }
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email || ''}
            </Typography>
            {user?.admin && (
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                Administrator
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={isEditing ? formData.firstName : (user?.firstName || '')}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              disabled={!isEditing}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={isEditing ? formData.lastName : (user?.lastName || '')}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              disabled={!isEditing}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={user?.email || ''}
              disabled={true}
              sx={{ mb: 2 }}
              helperText="Email changes require verification. Please use account settings."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Username"
              value={user?.username || ''}
              disabled={true}
              sx={{ mb: 2 }}
              helperText="Username cannot be changed"
            />
          </Grid>
        </Grid>

        {!isEditing && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Account expiration: {user?.expirationDate ? new Date(user.expirationDate).toLocaleDateString() : 'N/A'}
            </Typography>
            {user?.roles && user.roles.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Roles: {user.roles.join(', ')}
              </Typography>
            )}
          </Box>
        )}

        {/* Password Change Section - Only for LOCAL users */}
        {user?.provider === 'LOCAL' && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ mr: 1 }} />
                <Typography>Change Password</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Current Password"
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="New Password"
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="Password must be at least 8 characters with uppercase, lowercase, digit, and special character"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm New Password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    startIcon={<LockIcon />}
                  >
                    Change Password
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            startIcon={<EditIcon />}
            variant="outlined"
          >
            Edit Profile
          </Button>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              startIcon={<SaveIcon />}
              variant="contained"
            >
              Save Changes
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog; 